import { Button, Modal } from "@mantine/core";
import { useState } from "react";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import type {
  OAuthApplicationCreate,
  OAuthApplicationCreateResponse
} from "../ApiSettings.types";
import { useOAuthApplicationOptionsForCreate } from "../hooks/useOAuthApplicationOptionsForCreate";
import { useOAuthApplicationsMutations } from "../hooks/useOAuthApplicationsMutations";
import { OAuthApplicationForm } from "../OAuthApplicationForm/OAuthApplicationForm";
import { OAuthApplicationSuccess } from "./OAuthApplicationSuccess/OAuthApplicationSuccess";

interface CreateOAuthApplicationModalProps {
  isOpen?: boolean;
  onClose: () => void;
}

function CreateOAuthApplicationModal({
  isOpen,
  onClose
}: CreateOAuthApplicationModalProps) {
  const { showErrorToast } = useToast();
  const [createdApplication, setCreatedApplication] =
    useState<OAuthApplicationCreateResponse | null>(null);
  const { optionsForCreate, isLoading, error } =
    useOAuthApplicationOptionsForCreate();
  const { createMutation } = useOAuthApplicationsMutations();
  const customers = optionsForCreate?.customers ?? {};
  const scopes = optionsForCreate?.scope ?? [];

  async function handleSubmit(newApplication: OAuthApplicationCreate) {
    try {
      const response = await createMutation.mutateAsync(newApplication);
      setCreatedApplication(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        return Promise.reject(error);
      }

      showErrorToast(error);
      return Promise.resolve();
    }

    return Promise.resolve();
  }

  function handleCloseModal() {
    setCreatedApplication(null);
    onClose();
  }

  return (
    <Modal
      className="CreateOAuthApplicationModal"
      opened={isOpen || false}
      title="Neue OAuth-Applikation registrieren"
      onClose={handleCloseModal}
    >
      {!createdApplication ? (
        <LoadOrError error={error} loading={isLoading}>
          <OAuthApplicationForm
            customers={customers}
            scopes={scopes}
            onCancel={handleCloseModal}
            onSubmit={handleSubmit}
          />
        </LoadOrError>
      ) : (
        <OAuthApplicationSuccess
          clientId={createdApplication.client_id}
          clientSecret={createdApplication.client_secret}
          intro="Die OAuth-Applikation wurde erfolgreich registriert."
        />
      )}
      {createdApplication && (
        <ModalFooter>
          <Button variant="default" onClick={handleCloseModal}>
            Schließen
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}

export { CreateOAuthApplicationModal, CreateOAuthApplicationModalProps };
